import { useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  PcseEyeRx,
  PcseVoucherSupplement,
  PcseVoucherType,
} from '../../@types/pcse';
import {
  useNhsVoucherRates,
  useVoucherQuote,
  VOUCHER_SUPPLEMENTS,
} from '../claims/nhsVoucherRates';
import { describeEye } from '../claims/readPrescription';

const SUPPLEMENT_KEYS = Object.keys(
  VOUCHER_SUPPLEMENTS
) as PcseVoucherSupplement[];

/** The NHS voucher applied to a dispense, as the API expects it. */
export interface NhsVoucherSelection {
  type: PcseVoucherType;
  supplements?: PcseVoucherSupplement[];
  /** Lenses the per-lens supplements (prism/tint) were dispensed on. */
  lensesAffected?: number;
}

interface NhsVoucherPickerProps {
  /** Gross retail price of the appliance, before the voucher. */
  chargedAmount: number;
  value: NhsVoucherSelection | null;
  onChange: (next: NhsVoucherSelection | null) => void;
  /** Patient's current refraction — lets the server derive the band. */
  prescription?: { right?: PcseEyeRx; left?: PcseEyeRx; found?: boolean };
  /** Date the appliance is collected; decides which rate period values it. */
  suppliedDate?: string;
  disabled?: boolean;
  /** Net patient contribution, reported up so the caller can show it. */
  onNetChange?: (net: number | null) => void;
}

const money = (n: number) => `£${(Number(n) || 0).toFixed(2)}`;

/**
 * Applies an NHS optical voucher to a dispense and shows what the patient
 * actually owes.
 *
 * Every figure here is computed by the server against the published rate table
 * — deliberately, because the same calculation later builds the GOS3 claim. A
 * client-side estimate could only ever disagree with what is actually claimed,
 * which is the difference between a discount the practice recovers and one it
 * absorbs.
 */
export default function NhsVoucherPicker({
  chargedAmount,
  value,
  onChange,
  prescription,
  suppliedDate,
  disabled = false,
  onNetChange,
}: NhsVoucherPickerProps) {
  const enabled = value != null;

  const {
    typeOptions,
    loading: ratesLoading,
    error: ratesError,
    unconfigured,
  } = useNhsVoucherRates(suppliedDate);

  const hasRx = Boolean(prescription?.found);

  // The prescription is always sent so the server can derive the band; a band
  // the dispenser has picked is sent too and wins, while `derivedType` still
  // reports what the prescription implied so a mismatch stays visible.
  const { quote, loading: quoteLoading } = useVoucherQuote(
    enabled
      ? {
          right: prescription?.right,
          left: prescription?.left,
          type: value?.type || undefined,
          supplements: value?.supplements ?? [],
          lensesAffected: value?.lensesAffected,
          chargedAmount: chargedAmount || 0,
          onDate: suppliedDate,
        }
      : null
  );

  const derivedType = quote?.band?.derivedType;

  // Adopt the derived band once, as a default the dispenser can still change.
  useEffect(() => {
    if (enabled && derivedType && !value?.type) {
      onChange({ ...(value as NhsVoucherSelection), type: derivedType });
    }
  }, [enabled, derivedType, value, onChange]);

  const net = enabled && quote ? quote.patientContribution : null;
  useEffect(() => {
    onNetChange?.(net);
  }, [net, onNetChange]);

  const bandOverridden = Boolean(
    derivedType && value?.type && value.type !== derivedType
  );

  /**
   * The appliance costs less than the voucher is worth, so the NHS pays only
   * what was charged and the balance is lost. Usually a sign the price is
   * wrong rather than that the practice is giving change.
   */
  const capped = Boolean(quote && quote.redeemedValue < quote.voucherValue);

  const supplements = useMemo(
    () => value?.supplements ?? [],
    [value?.supplements]
  );

  const toggle = (key: PcseVoucherSupplement) => {
    if (!value) return;
    const next = supplements.includes(key)
      ? supplements.filter((s) => s !== key)
      : [...supplements, key];
    onChange({ ...value, supplements: next });
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          NHS optical voucher
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              disabled={disabled}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? { type: '' as PcseVoucherType, supplements: [] }
                    : null
                )
              }
            />
          }
          label={enabled ? 'Applied' : 'Not applied'}
        />
      </Stack>

      {!enabled && (
        <Typography variant="body2" color="text.secondary">
          Turn this on for an NHS voucher supply. The voucher comes off what the
          patient pays at the till and is recovered from PCSE on a GOS3 claim.
        </Typography>
      )}

      {enabled && (
        <Stack spacing={2}>
          {(ratesError || unconfigured) && !ratesLoading && (
            <Alert severity="warning">
              {ratesError ??
                'No NHS voucher values are configured, so this voucher is worth £0.00.'}{' '}
              Set them under Settings → NHS Voucher Rates.
            </Alert>
          )}

          {hasRx ? (
            <Alert severity="success">
              Band derived from the patient&apos;s refraction — R{' '}
              <strong>{describeEye(prescription!.right ?? {})}</strong> · L{' '}
              <strong>{describeEye(prescription!.left ?? {})}</strong>
              {quote?.band?.reason && (
                <>
                  {' '}
                  → band <strong>{quote.band.derivedType}</strong> (
                  {quote.band.reason}).
                </>
              )}
            </Alert>
          ) : (
            <Alert severity="warning">
              No refraction found for this patient, so the band cannot be
              derived — choose it below.
            </Alert>
          )}

          {bandOverridden && (
            <Alert severity="warning">
              You have selected band <strong>{value?.type}</strong>, but this
              prescription gives band <strong>{derivedType}</strong>. The
              selected band is what will be discounted and claimed.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Voucher band"
                disabled={disabled || ratesLoading}
                value={value?.type ?? ''}
                onChange={(e) =>
                  onChange({
                    ...(value as NhsVoucherSelection),
                    type: e.target.value as PcseVoucherType,
                  })
                }
                helperText={
                  ratesLoading ? 'Loading published values…' : undefined
                }
              >
                {typeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Lenses supplements apply to"
                disabled={disabled}
                value={value?.lensesAffected ?? 2}
                inputProps={{ min: 0, max: 2, step: 1 }}
                onChange={(e) =>
                  onChange({
                    ...(value as NhsVoucherSelection),
                    lensesAffected: Number(e.target.value),
                  })
                }
                helperText="A prism or tint prescribed for one eye claims half as much."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Supplements
              </Typography>
              <Stack direction="row" flexWrap="wrap">
                {SUPPLEMENT_KEYS.map((key) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        size="small"
                        disabled={disabled}
                        checked={supplements.includes(key)}
                        onChange={() => toggle(key)}
                      />
                    }
                    label={VOUCHER_SUPPLEMENTS[key].label}
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>

          <Divider />

          {/* ── What the patient actually pays ──────────────────────── */}
          <Box>
            {quoteLoading && !quote && (
              <Typography variant="body2" color="text.secondary">
                Valuing the voucher…
              </Typography>
            )}

            {quote && (
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Chip
                    label={`Charged ${money(chargedAmount)}`}
                    variant="outlined"
                  />
                  <Chip
                    color="info"
                    label={`NHS pays ${money(quote.redeemedValue)}`}
                  />
                  <Chip
                    color="primary"
                    label={`Patient pays ${money(quote.patientContribution)}`}
                  />
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  Band {quote.band.type} is worth {money(quote.baseValue)}
                  {quote.supplementValue > 0 && (
                    <>
                      {' '}
                      + {money(quote.supplementValue)} supplements ={' '}
                      {money(quote.voucherValue)}
                    </>
                  )}
                  .{' '}
                  {capped ? (
                    <>
                      The appliance costs less than the voucher, so the NHS pays
                      only the {money(quote.redeemedValue)} charged — the{' '}
                      {money(quote.voucherValue - quote.redeemedValue)} balance
                      is not paid out.
                    </>
                  ) : (
                    <>
                      The till will take {money(quote.patientContribution)}; the{' '}
                      {money(quote.redeemedValue)} voucher is recovered from
                      PCSE on a GOS3 claim.
                    </>
                  )}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
